import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import useScrollSync from '../../../hooks/useScrollSync'

const computeAtScrollEnd = wrapper => {
  const maxScroll = wrapper.scrollWidth - wrapper.clientWidth
  return maxScroll <= 1 || wrapper.scrollLeft >= maxScroll - 1
}

// The in-flow scrollbar is trapped in the bordered frame, so it hides below the
// Fold while the table sits lower on the page. A position:fixed clone pinned to
// The viewport bottom covers that gap — shown while the table overflows and its
// Bottom edge is still below the fold (mirrors FloatingHeader's fixed thead clone).
const computeShowFloatingBar = wrapper => {
  if (wrapper.scrollWidth <= wrapper.clientWidth)
    return false
  const rect = wrapper.getBoundingClientRect()
  return rect.top < window.innerHeight && rect.bottom > window.innerHeight
}

// Tracks the table's width/scroll for the floating clone header, the fake bottom
// Scrollbar, and the actions-column separator toggle.
const useDataTableLayout = (rowCount, columnsSignature) => {
  const wrapperRef = useRef(null)
  const floatingBarRef = useRef(null)
  const floatingBarCloneRef = useRef(null)
  const cloneScrollRef = useRef(null)
  const [tableScrollWidth, setTableScrollWidth] = useState(0)
  const [wrapperRect, setWrapperRect] = useState({ left: 0, width: 0 })
  const [colWidths, setColWidths] = useState([])
  const [showCloneHeader, setShowCloneHeader] = useState(false)
  const [showFloatingBar, setShowFloatingBar] = useState(false)
  const [atScrollEnd, setAtScrollEnd] = useState(true)

  useEffect(() => {
    let cleanup = null
    const thead = wrapperRef.current && wrapperRef.current.querySelector('thead')
    if (thead) {
      const observer = new IntersectionObserver(([entry]) => {
        const aboveViewport = entry.boundingClientRect.bottom < 0
        setShowCloneHeader(!entry.isIntersecting && aboveViewport)
      }, { threshold: 0 })
      observer.observe(thead)
      cleanup = () => observer.disconnect()
    }
    return cleanup
  }, [])

  useEffect(() => {
    let cleanup = null
    const wrapper = wrapperRef.current
    if (wrapper) {
      const sync = () => {
        setTableScrollWidth(wrapper.scrollWidth)
        const rect = wrapper.getBoundingClientRect()
        setWrapperRect({ left: rect.left, width: wrapper.clientWidth })
        const ths = wrapper.querySelectorAll('thead > tr > th')
        setColWidths(Array.from(ths).map(th => th.getBoundingClientRect().width))
        setAtScrollEnd(computeAtScrollEnd(wrapper))
        setShowFloatingBar(computeShowFloatingBar(wrapper))
      }
      const onScroll = () => setShowFloatingBar(computeShowFloatingBar(wrapper))
      sync()
      const observer = new ResizeObserver(sync)
      observer.observe(wrapper)
      const table = wrapper.querySelector('table')
      if (table)
        observer.observe(table)
      window.addEventListener('resize', sync)
      window.addEventListener('scroll', onScroll, { passive: true })
      cleanup = () => {
        observer.disconnect()
        window.removeEventListener('resize', sync)
        window.removeEventListener('scroll', onScroll)
      }
    }
    return cleanup
  }, [rowCount, columnsSignature])

  const syncScroll = useScrollSync([wrapperRef, floatingBarRef, floatingBarCloneRef, cloneScrollRef])
  const handleScroll = event => {
    syncScroll(event)
    if (wrapperRef.current)
      setAtScrollEnd(computeAtScrollEnd(wrapperRef.current))
  }

  useLayoutEffect(() => {
    if (showCloneHeader && cloneScrollRef.current && wrapperRef.current)
      cloneScrollRef.current.scrollLeft = wrapperRef.current.scrollLeft
  }, [showCloneHeader])

  useLayoutEffect(() => {
    if (showFloatingBar && floatingBarCloneRef.current && wrapperRef.current)
      floatingBarCloneRef.current.scrollLeft = wrapperRef.current.scrollLeft
  }, [showFloatingBar])

  return {
    wrapperRef,
    floatingBarRef,
    floatingBarCloneRef,
    cloneScrollRef,
    tableScrollWidth,
    wrapperRect,
    colWidths,
    showCloneHeader,
    showFloatingBar,
    atScrollEnd,
    handleScroll
  }
}

export default useDataTableLayout
